import { ELSCommonConfig } from '../els.components';
import { SHERPATH_AI_ORIGIN, ALLOWED_MESSAGE_ORIGINS } from './sherpath-ai-chat-sidebar.constants';
import { EnvProfile } from '../app/server.constants';

/**
 * Gets the target origin for postMessage communication with the Sherpath AI iframe.
 *
 * In production environments, this returns the specific Sherpath AI origin URL for security.
 * In non-production environments (dev, qa, test, staging, etc.), this returns '*' to allow
 * communication with any origin for easier development and testing.
 *
 * @returns The origin URL to use for postMessage calls to the Sherpath AI iframe
 *          Returns '*' for non-production, specific origin for production
 *
 * @example
 * ```typescript
 * const message = { type: 'UPDATE', data: {...} };
 * iframe.contentWindow.postMessage(message, getSherpathAIOrigin());
 * ```
 */
export const getSherpathAIOrigin = (): string => {
  const isProduction = ELSCommonConfig.appProfile === EnvProfile.prod;

  // Use wildcard for non-production to allow flexible iframe sources during development
  // Use specific origin for production to enforce security
  return isProduction ? SHERPATH_AI_ORIGIN : '*';
};

/**
 * Validates if a message event origin is from an allowed/trusted source.
 *
 * In production environments, validates against the ALLOWED_MESSAGE_ORIGINS list.
 * Supports two matching strategies:
 * 1. Exact origin match (e.g., 'https://www.osmosis.org')
 * 2. Domain suffix match (e.g., '.elsevier.com' matches 'https://nhe-ai.elsevier.com')
 *
 * In non-production environments (dev, qa, test, staging, etc.), allows all origins
 * for easier development and testing.
 *
 * @param origin The origin from the MessageEvent (event.origin)
 * @returns true if the origin is allowed, false otherwise
 *
 * @example
 * ```typescript
 * const handleMessage = (event: MessageEvent) => {
 *   if (!isAllowedMessageOrigin(event.origin)) {
 *     return;
 *   }
 *   // Process the message
 * };
 * ```
 */
export const isAllowedMessageOrigin = (origin: string): boolean => {
  // In non-production, allow all origins for development flexibility
  if (ELSCommonConfig.appProfile !== EnvProfile.prod) {
    return true;
  }

  // In production, validate against allowlist
  return ALLOWED_MESSAGE_ORIGINS.some((allowedOrigin) => {
    // If allowedOrigin starts with a dot, it's a domain suffix pattern
    if (allowedOrigin.startsWith('.')) {
      // Extract hostname from origin URL (e.g., 'https://nhe-ai.elsevier.com' -> 'nhe-ai.elsevier.com')
      try {
        const { hostname } = new URL(origin);
        // Check if hostname ends with the allowed domain suffix
        return hostname.endsWith(allowedOrigin) || hostname === allowedOrigin.slice(1);
      } catch {
        // Invalid origin URL format
        return false;
      }
    }

    // Otherwise, exact match
    return origin === allowedOrigin;
  });
};
